/**
 * L1 안전 검사 — 올린 JSON(3D 정의 · 설비 일괄 파일 · 서버 import 본문) 전체를 재귀로 훑어
 *   · 객체 키 `__proto__` · `prototype` · `constructor` 를 거부한다(프로토타입 오염 · 복사 시 사고 방지)
 *   · 숫자 NaN · Infinity · -Infinity 를 거부한다(JSON.parse 는 만들지 않지만 JS 객체로 들어오는 경로가 있다)
 *   · 너무 깊은 중첩을 거부한다(재귀 폭주 방지)
 * 의존성 없음 — 서버(lib/generated/twin-definition/safeJson.js, scripts/gen-twin-validator.js 가 만든다)도 같은 코드를 쓴다.
 */
import type { Issue } from "./types";

export const FORBIDDEN_KEYS: readonly string[] = ["__proto__", "prototype", "constructor"];
export const MAX_JSON_DEPTH = 64;

/** 문제를 경로와 함께 모은다(최대 limit 건). 없으면 빈 배열 */
export function scanUnsafeJson(raw: unknown, root = "", limit = 20): Issue[] {
  const out: Issue[] = [];
  const seen = new Set<unknown>();
  const at = (path: string) => path || "(맨 바깥)";
  const walk = (v: unknown, path: string, depth: number) => {
    if (out.length >= limit) return;
    if (typeof v === "number") { if (!Number.isFinite(v)) out.push({ path: at(path), message: "숫자가 아닙니다(NaN · Infinity 는 쓸 수 없습니다)" }); return; }
    if (!v || typeof v !== "object") return;
    if (depth > MAX_JSON_DEPTH) { out.push({ path: at(path), message: `너무 깊게 겹쳐 있습니다(${MAX_JSON_DEPTH}단까지)` }); return; }
    if (seen.has(v)) return; // 순환 참조(JS 객체로 들어온 경우) — 한 번만 본다
    seen.add(v);
    if (Array.isArray(v)) { for (let i = 0; i < v.length && out.length < limit; i++) walk(v[i], `${path}[${i}]`, depth + 1); return; }
    for (const k of Object.keys(v as Record<string, unknown>)) {
      if (out.length >= limit) return;
      const p = path ? `${path}.${k}` : k;
      if (FORBIDDEN_KEYS.includes(k)) { out.push({ path: at(p), message: `「${k}」 키는 쓸 수 없습니다(보안)` }); continue; }
      walk((v as Record<string, unknown>)[k], p, depth + 1);
    }
  };
  walk(raw, root, 0);
  return out;
}
